export const PAID_ATTRIBUTION_MODEL_VERSION = "paid_attribution_v2_acquisition_first";

export const GOOGLE_CLICK_ID_KEYS = ["gclid", "gbraid", "wbraid"];
export const META_CLICK_ID_KEYS = ["fbclid"];
export const PAID_CLICK_ID_KEYS = [
  ...GOOGLE_CLICK_ID_KEYS,
  ...META_CLICK_ID_KEYS,
];

const MAX_TOUCHPOINTS = 12;
const MAX_TEXT_LENGTH = 180;

const PROVIDER_CONFIG = {
  google: {
    provider: "google",
    source: "google",
    adPlatform: "google_ads",
    sourceFamily: "google",
  },
  meta: {
    provider: "meta",
    source: "facebook",
    adPlatform: "meta_ads",
    sourceFamily: "meta",
  },
};

const SOURCE_ALIASES = {
  adwords: "google",
  google: "google",
  google_ads: "google",
  googleads: "google",
  paid_google: "google",
  fb: "meta",
  facebook: "meta",
  facebook_ads: "meta",
  facebookads: "meta",
  instagram: "meta",
  ig: "meta",
  meta: "meta",
  meta_ads: "meta",
  metaads: "meta",
};

const CLICK_ID_PROVIDER = {
  gclid: "google",
  gbraid: "google",
  wbraid: "google",
  fbclid: "meta",
};

const cleanText = (value, maxLength = MAX_TEXT_LENGTH) =>
  String(value ?? "")
    .trim()
    .slice(0, maxLength);

const cleanToken = (value = "") =>
  cleanText(value, 80)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const normalizeProvider = (...values) => {
  for (const value of values) {
    const token = cleanToken(value);
    if (!token) continue;
    if (SOURCE_ALIASES[token]) return SOURCE_ALIASES[token];
  }
  return "";
};

const clickIdTypesForProvider = (clickIds = {}, provider = "") =>
  PAID_CLICK_ID_KEYS.filter(
    (key) => CLICK_ID_PROVIDER[key] === provider && cleanText(clickIds[key], 512),
  );

const allClickIdTypes = (clickIds = {}) =>
  PAID_CLICK_ID_KEYS.filter((key) => cleanText(clickIds[key], 512));

const latestClickProvider = (clickTypes = [], clickIdCapturedAtMs = {}) => {
  const best = clickTypes.reduce(
    (current, key, index) => {
      const capturedAtMs = numberOrNull(clickIdCapturedAtMs[key]) || 0;
      if (capturedAtMs > current.capturedAtMs) {
        return { key, index, capturedAtMs };
      }
      if (capturedAtMs === current.capturedAtMs && current.index > index) {
        return { key, index, capturedAtMs };
      }
      return current;
    },
    { key: "", index: Number.POSITIVE_INFINITY, capturedAtMs: 0 },
  );
  return CLICK_ID_PROVIDER[best.key] || "";
};

const numberOrNull = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? Math.floor(number) : null;
};

const compactObject = (value = {}) =>
  Object.fromEntries(
    Object.entries(value).filter(([, item]) => {
      if (item === null || item === undefined) return false;
      if (typeof item === "string") return item.trim() !== "";
      if (Array.isArray(item)) return item.length > 0;
      if (typeof item === "object") return Object.keys(item).length > 0;
      return true;
    }),
  );

const compactUtm = (utm = {}) =>
  compactObject({
    utm_source: cleanText(utm.utm_source || utm.utmSource, 80),
    utm_medium: cleanText(utm.utm_medium || utm.utmMedium, 80),
    utm_campaign: cleanText(utm.utm_campaign || utm.utmCampaign, 180),
    utm_term: cleanText(utm.utm_term || utm.utmTerm, 180),
    utm_content: cleanText(utm.utm_content || utm.utmContent, 180),
  });

export const buildPaidAttributionTouchpoint = ({
  source = "",
  clickIds = {},
  clickIdCapturedAtMs = {},
  utm = {},
  landingPage = "",
  referrer = "",
  capturedAtMs = null,
} = {}) => {
  const clickTypes = allClickIdTypes(clickIds);
  const clickProvider = latestClickProvider(clickTypes, clickIdCapturedAtMs);
  if (!clickProvider && cleanToken(utm.utm_medium || utm.utmMedium) === "organic") {
    return null;
  }
  const sourceProvider = normalizeProvider(source, utm.utm_source, utm.utmSource);
  const provider = clickProvider || sourceProvider;
  const config = PROVIDER_CONFIG[provider];
  if (!config) return null;

  const providerClickTypes = clickIdTypesForProvider(clickIds, provider);
  const latestClickMs = providerClickTypes.reduce((latest, key) => {
    const captured = numberOrNull(clickIdCapturedAtMs[key]);
    return captured && captured > latest ? captured : latest;
  }, 0);
  const touchpointMs =
    latestClickMs || numberOrNull(capturedAtMs) || numberOrNull(Date.now());
  const basis = providerClickTypes.length ? "paid_click_id" : "paid_utm";

  return compactObject({
    provider: config.provider,
    source: config.source,
    sourceFamily: config.sourceFamily,
    adPlatform: config.adPlatform,
    attributionBasis: basis,
    confidence: providerClickTypes.length ? "click_id" : "utm_paid",
    capturedAtMs: touchpointMs,
    clickIds: providerClickTypes.reduce((acc, key) => {
      const value = cleanText(clickIds[key], 512);
      if (value) acc[key] = value;
      return acc;
    }, {}),
    clickIdTypes: providerClickTypes,
    utm: compactUtm(utm),
    landingPage: cleanText(landingPage, 220),
    referrer: cleanText(referrer, 220),
  });
};

export const appendPaidAttributionTouchpoint = ({
  previousTouchpoints = [],
  touchpoint = null,
} = {}) => {
  if (!touchpoint) return Array.isArray(previousTouchpoints) ? previousTouchpoints : [];
  const existing = Array.isArray(previousTouchpoints) ? previousTouchpoints : [];
  const key = [
    touchpoint.provider,
    touchpoint.capturedAtMs,
    ...(touchpoint.clickIdTypes || []).map((type) => touchpoint.clickIds?.[type] || type),
  ].join("|");
  const withoutDuplicate = existing.filter((item) => {
    const itemKey = [
      item?.provider,
      item?.capturedAtMs,
      ...(item?.clickIdTypes || []).map((type) => item?.clickIds?.[type] || type),
    ].join("|");
    return itemKey !== key;
  });
  const sorted = [...withoutDuplicate, touchpoint].sort(
    (left, right) => Number(left?.capturedAtMs || 0) - Number(right?.capturedAtMs || 0),
  );
  if (sorted.length <= MAX_TOUCHPOINTS) return sorted;
  return [sorted[0], ...sorted.slice(-(MAX_TOUCHPOINTS - 1))];
};

const legacyTouchpointFromStoredAttribution = (storedAttribution = {}) =>
  buildPaidAttributionTouchpoint({
    source: storedAttribution.source || storedAttribution.utmSource,
    clickIds: storedAttribution.clickIds || {},
    clickIdCapturedAtMs: storedAttribution.clickIdCapturedAtMs || {},
    utm: storedAttribution.utm || storedAttribution,
    landingPage: storedAttribution.landingPage,
    referrer: storedAttribution.referrer,
    capturedAtMs: storedAttribution.capturedAtMs,
  });

const legacyTouchpointsFromStoredAttribution = (storedAttribution = {}) => {
  const clickIds =
    storedAttribution?.clickIds && typeof storedAttribution.clickIds === "object"
      ? storedAttribution.clickIds
      : {};
  const clickIdCapturedAtMs = storedAttribution?.clickIdCapturedAtMs || {};
  const providerTouchpoints = Object.entries(PROVIDER_CONFIG)
    .map(([provider, config]) => {
      const providerClickIds = PAID_CLICK_ID_KEYS.reduce((acc, key) => {
        if (CLICK_ID_PROVIDER[key] === provider && cleanText(clickIds[key], 512)) {
          acc[key] = clickIds[key];
        }
        return acc;
      }, {});
      if (!Object.keys(providerClickIds).length) return null;
      const providerCapturedAtMs = Object.keys(providerClickIds).reduce((latest, key) => {
        const captured = numberOrNull(clickIdCapturedAtMs[key]);
        return captured && captured > latest ? captured : latest;
      }, 0);
      // Legacy storage merged several providers into one root UTM snapshot.
      // Bind that snapshot only to the matching, contemporaneous provider.
      const legacyUtm = storedAttribution.utm || storedAttribution;
      const utmProvider = normalizeProvider(legacyUtm.utm_source, legacyUtm.utmSource);
      const ownsUtm = providerCapturedAtMs > 0 &&
        providerCapturedAtMs === numberOrNull(storedAttribution.capturedAtMs) &&
        (!utmProvider || utmProvider === provider);
      return buildPaidAttributionTouchpoint({
        source: config.source,
        clickIds: providerClickIds,
        clickIdCapturedAtMs,
        utm: ownsUtm ? legacyUtm : {},
        landingPage: storedAttribution.landingPage,
        referrer: storedAttribution.referrer,
        capturedAtMs: providerCapturedAtMs || storedAttribution.capturedAtMs,
      });
    })
    .filter(Boolean);
  if (providerTouchpoints.length) return providerTouchpoints;
  const fallback = legacyTouchpointFromStoredAttribution(storedAttribution);
  return fallback ? [fallback] : [];
};

const touchRank = (touchpoint = {}) => ({
  capturedAtMs: Number(touchpoint.capturedAtMs || 0),
  proofRank: touchpoint.attributionBasis === "paid_click_id" ? 2 : 1,
});

const pickPaidTouchpoint = (touchpoints = [], mode = "acquisition") =>
  touchpoints.reduce((best, item) => {
    if (!best) return item;
    const bestRank = touchRank(best);
    const nextRank = touchRank(item);
    if (mode === "conversion") {
      if (nextRank.capturedAtMs > bestRank.capturedAtMs) return item;
      if (nextRank.capturedAtMs < bestRank.capturedAtMs) return best;
      return nextRank.proofRank > bestRank.proofRank ? item : best;
    }
    if (nextRank.capturedAtMs < bestRank.capturedAtMs) return item;
    if (nextRank.capturedAtMs > bestRank.capturedAtMs) return best;
    return nextRank.proofRank > bestRank.proofRank ? item : best;
  }, null);

const resolveSelfReportDisagreementKind = ({
  acquisitionProvider = "",
  selfProvider = "",
  selfReported = "",
} = {}) => {
  if (acquisitionProvider) {
    if (!selfReported) return "";
    if (selfProvider && selfProvider === acquisitionProvider) {
      return "acquisition_self_report_same";
    }
    if (selfProvider && selfProvider !== acquisitionProvider) {
      return "acquisition_self_report_paid_provider_mismatch";
    }
    return "acquisition_paid_self_report_nonpaid";
  }
  if (selfReported) return "self_report_only_no_click_proof";
  return "unattributed";
};

export const resolvePaidAttribution = ({
  storedAttribution = null,
  touchpoints = null,
  selfReportedAdSource = "",
} = {}) => {
  const sourceTouchpoints = Array.isArray(touchpoints)
    ? touchpoints
    : Array.isArray(storedAttribution?.touchpoints)
      ? storedAttribution.touchpoints
      : [];
  const legacyTouchpoints = sourceTouchpoints.length
    ? []
    : legacyTouchpointsFromStoredAttribution(storedAttribution || {});
  const paidTouchpoints = [...sourceTouchpoints, ...legacyTouchpoints]
    .filter(Boolean)
    .map((item) =>
      buildPaidAttributionTouchpoint({
        ...item,
        source: item.source,
        clickIds: item.clickIds || {},
        clickIdCapturedAtMs: item.clickIdCapturedAtMs || {},
        utm: item.utm || item,
        capturedAtMs: item.capturedAtMs,
        landingPage: item.landingPage,
        referrer: item.referrer,
      }),
    )
    .filter(Boolean);

  const acquisition = pickPaidTouchpoint(paidTouchpoints, "acquisition");
  const conversion = pickPaidTouchpoint(paidTouchpoints, "conversion");

  const selfProvider = normalizeProvider(selfReportedAdSource);
  const selfConfig = PROVIDER_CONFIG[selfProvider];
  const selfReported = cleanText(selfReportedAdSource, 80);
  const allClickTypes = Array.from(
    new Set(paidTouchpoints.flatMap((item) => item.clickIdTypes || [])),
  );

  if (!acquisition && selfReported) {
    return {
      attributionModelVersion: PAID_ATTRIBUTION_MODEL_VERSION,
      source: selfConfig?.source || cleanToken(selfReported) || "self_reported",
      sourceFamily: selfConfig?.sourceFamily || "self_reported",
      attributionBasis: "self_reported_only",
      confidence: "self_reported_only",
      selfReportedAdSource: selfReported,
      attributionDisagreement: false,
      acquisitionSelfReportDisagreementKind: "self_report_only_no_click_proof",
      acquisitionConversionDisagreement: false,
      assistedProviders: [],
      touchpoints: [],
    };
  }

  if (!acquisition) {
    return {
      attributionModelVersion: PAID_ATTRIBUTION_MODEL_VERSION,
      attributionBasis: "unattributed",
      confidence: "none",
      selfReportedAdSource: selfReported,
      attributionDisagreement: false,
      acquisitionSelfReportDisagreementKind: "unattributed",
      acquisitionConversionDisagreement: false,
      assistedProviders: [],
      touchpoints: [],
    };
  }

  const assistedProviders = Array.from(
    new Set(
      paidTouchpoints
        .filter((item) => item.provider && item.provider !== acquisition.provider)
        .map((item) => item.provider),
    ),
  );
  const selfReportKind = resolveSelfReportDisagreementKind({
    acquisitionProvider: acquisition.provider,
    selfProvider,
    selfReported,
  });
  const disagreement =
    selfReportKind === "acquisition_self_report_paid_provider_mismatch" ||
    selfReportKind === "acquisition_paid_self_report_nonpaid";
  const acquisitionConversionDisagreement = Boolean(
    conversion?.provider && conversion.provider !== acquisition.provider,
  );

  return compactObject({
    attributionModelVersion: PAID_ATTRIBUTION_MODEL_VERSION,
    acquisitionProvider: acquisition.provider,
    acquisitionSource: acquisition.source,
    conversionProvider: conversion?.provider,
    conversionSource: conversion?.source,
    primaryPaidProvider: acquisition.provider,
    primaryAdSource: acquisition.source,
    source: acquisition.source,
    sourceFamily: acquisition.sourceFamily,
    paidMediaProvider: acquisition.provider,
    adPlatform: acquisition.adPlatform,
    attributionBasis: acquisition.attributionBasis,
    confidence: acquisition.confidence,
    conversionAttributionBasis: conversion?.attributionBasis,
    conversionConfidence: conversion?.confidence,
    firstPaidClickAtMs: acquisition.capturedAtMs,
    latestPaidClickAtMs: conversion?.capturedAtMs,
    hasAdClickId: allClickTypes.length > 0,
    hasGoogleClickId: allClickTypes.some((key) =>
      GOOGLE_CLICK_ID_KEYS.includes(key),
    ),
    hasMetaClickId: allClickTypes.includes("fbclid"),
    clickIdTypes: allClickTypes,
    assistedProviders,
    selfReportedAdSource: selfReported,
    attributionDisagreement: disagreement,
    acquisitionSelfReportDisagreementKind: selfReportKind,
    acquisitionConversionDisagreement,
    utmSource: cleanToken(acquisition.utm?.utm_source),
    utmMedium: cleanToken(acquisition.utm?.utm_medium),
    utmCampaign: cleanText(acquisition.utm?.utm_campaign, 180),
    utmTerm: cleanText(acquisition.utm?.utm_term, 180),
    utmContent: cleanText(acquisition.utm?.utm_content, 180),
    touchpoints: paidTouchpoints.map((item) =>
      compactObject({
        provider: item.provider,
        source: item.source,
        attributionBasis: item.attributionBasis,
        confidence: item.confidence,
        capturedAtMs: item.capturedAtMs,
        clickIdTypes: item.clickIdTypes || [],
        utmSource: cleanText(item.utm?.utm_source, 80),
        utmMedium: cleanText(item.utm?.utm_medium, 80),
        utmCampaign: cleanText(item.utm?.utm_campaign, 180),
        utmTerm: cleanText(item.utm?.utm_term, 180),
        utmContent: cleanText(item.utm?.utm_content, 180),
      }),
    ),
  });
};
